import { useEffect } from 'react'
import { Link, usePage, router } from '@inertiajs/react'
import {
  Building2,
  Users,
  Activity,
  Shield,
  LogOut,
} from 'lucide-react'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { TooltipProvider } from '@/components/ui/tooltip'
import { toast } from 'sonner'
import NavIcon, { type NavItem } from '@/components/shared/NavIcon'
import ImpersonationBanner from '@/components/shared/ImpersonationBanner'

const ADMIN_NAV: NavItem[] = [
  { name: 'Organizations', href: '/admin/organizations', icon: Building2 },
  { name: 'Users', href: '/admin/users', icon: Users },
  { name: 'System Status', href: '/admin/system_status', icon: Activity },
]

export default function AdminLayout({ children }: { children: React.ReactNode }) {
  const { url, props } = usePage<{
    current_user: { name: string | null; email_address: string; role: string; super_admin: boolean } | null
    current_organization?: { slug: string } | null
    impersonating_org: { id: string; name: string } | null
    flash: { notice?: string; alert?: string }
  }>()
  const { flash } = props as { flash: { notice?: string; alert?: string } }

  useEffect(() => {
    if (flash?.notice) toast.success(flash.notice)
    if (flash?.alert) toast.error(flash.alert)
  }, [flash?.notice, flash?.alert])

  function isActive(href: string) {
    if (href === '/admin') return url === '/admin'
    return url.startsWith(href)
  }

  return (
    <TooltipProvider delayDuration={0}>
      <div className="flex h-screen flex-col">
        {/* Impersonation banner */}
        {props.impersonating_org && (
          <ImpersonationBanner orgName={props.impersonating_org.name} />
        )}
        <div className="flex flex-1 min-h-0">
          {/* Sidebar */}
          <nav className="flex w-14 flex-col items-center gap-1 border-r bg-muted/50 py-4">
            {/* Logo */}
            <Link href="/admin" className="mb-4 flex h-8 w-8 items-center justify-center rounded-md bg-primary">
              <Shield className="h-4 w-4 text-primary-foreground" />
            </Link>

            {/* Nav items */}
            {ADMIN_NAV.map((item) => (
              <NavIcon key={item.href} item={item} active={isActive(item.href)} />
            ))}

            {/* Spacer */}
            <div className="flex-1" />

            {/* User menu popover (bottom) */}
            {props.current_user && (
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-xs font-medium text-primary-foreground cursor-pointer"
                  >
                    {(props.current_user.name || props.current_user.email_address)
                      .charAt(0)
                      .toUpperCase()}
                  </button>
                </PopoverTrigger>
                <PopoverContent side="right" align="end" className="w-48 p-1 gap-1">
                  <div className="px-2.5 py-1.5">
                    <p className="text-[13px] font-semibold text-foreground">{props.current_user.name || 'User'}</p>
                    <p className="text-[11px] text-muted-foreground">{props.current_user.email_address}</p>
                    <p className="text-[10px] font-medium text-amber-600 mt-0.5">Super Admin</p>
                  </div>
                  <div className="h-px bg-border" />
                  <Link href="/admin/account" className="flex items-center gap-2 rounded-md px-2.5 py-1.5 text-[13px] text-foreground hover:bg-accent cursor-pointer">
                    <Users className="h-3.5 w-3.5 text-muted-foreground" />
                    Account
                  </Link>
                  <div className="h-px bg-border" />
                  <button
                    onClick={() => router.delete('/session')}
                    className="flex w-full items-center gap-2 rounded-md px-2.5 py-1.5 text-[13px] text-red-600 hover:bg-red-50 cursor-pointer"
                  >
                    <LogOut className="h-3.5 w-3.5" />
                    Logout
                  </button>
                </PopoverContent>
              </Popover>
            )}
          </nav>

          {/* Main content */}
          <main className="flex flex-1 flex-col overflow-hidden">
            {children}
          </main>
        </div>
      </div>
    </TooltipProvider>
  )
}
